// Class that constructs a new AudioContext that will allow us
// to mute the player at certain intervals. The AudioContext represents
// the sound system of the computer and is the main object used for
// creating and managing audio. Audio is generated by a set of
// AudioNodes that are combined and routed to the AudioDestinationNode.
// https://developer.mozilla.org/en-US/docs/Web/API/AudioContext

import { isFirefox } from 'utilities/device';

export default class FillerVolumeControl {
  videoRef = null;
  fillerRanges = [];
  disabled;

  constructor({ videoRef, fillerRanges }) {
    this.videoRef = videoRef;
    this.fillerRanges = fillerRanges;
  }

  // Called when we change the fillerWordRemoval to 'text' or 'none'
  disable = () => {
    if (this.disabled === undefined) {
      return;
    }

    this.disabled = true;

    this.cancelScheduledProcessing();
    this.suspendAudioProcess();
  };

  // Called when we change the fillerWordRemoval to 'text_audio'
  enable = fillerRanges => {
    this.fillerRanges = fillerRanges;

    if (this.disabled) {
      this.disabled = false;

      return;
    }

    this.disabled = false;

    this.videoRef.addEventListener('playing', this.resumeAudioProcess);
    this.videoRef.addEventListener('ratechange', this.resumeAudioProcess);
    this.videoRef.addEventListener('pause', this.suspendAudioProcess);
    this.videoRef.addEventListener('ended', this.suspendAudioProcess);
    this.videoRef.addEventListener('waiting', this.suspendAudioProcess);
  };

  // On pause or end event of the player
  // The suspend() method of the AudioContext Interface suspends
  // the progression of time in the audio context,
  // temporarily halting audio hardware access and reducing
  // CPU/battery usage in the process — this is useful if you want
  // an application to power down the audio hardware when
  // it will not be using an audio context for a while.
  // https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/suspend
  suspendAudioProcess = () => {
    if (this.audioCtx) {
      if (this.gainNode) {
        this.gainNode.gain.setValueAtTime(1, this.audioCtx.currentTime);
      }

      this.audioCtx.suspend();
    }
  };

  // On playing or ratechange of the player we want to resume the audio process
  resumeAudioProcess = async () => {
    await this.connectAudioCtx();

    if (!this.disabled) {
      const { currentTime: audioTime } = this.audioCtx;

      this.cancelScheduledProcessing();

      this.gainNode.gain.setValueAtTime(1, audioTime);

      this.setMutedTimes();
    }
  };

  // The cancelScheduledValues() method of the AudioParam Interface
  // cancels all scheduled future changes to the AudioParam.
  // https://developer.mozilla.org/en-US/docs/Web/API/AudioParam/cancelScheduledValues
  cancelScheduledProcessing = () => {
    this.gainNode?.gain.cancelScheduledValues(this.audioCtx.currentTime);
  };

  // Schedules out the times that we want to mute the player based
  // on the future fillerRanges
  setMutedTimes = () => {
    const { currentTime, playbackRate } = this.videoRef;
    const { currentTime: audioTime } = this.audioCtx;

    // We cannot set the gainNode if the playbackRate is zero
    // This mostly happens when the video is starting
    if (playbackRate === 0) {
      return;
    }

    // Filter and return all the fillerRanges that are in the future
    this.fillerRanges
      .filter(([s, e]) => {
        return s > currentTime || e > currentTime;
      })
      .forEach(([s, e]) => {
        // For each range calculate the delta time to the start of the filler word (from)
        // and the end of the filler word (to)
        // Since the timestamps from our provider are not always accurate add
        // 0.1s of padding to both from and to
        const from = Math.max(s - currentTime, 0) - 0.1;
        const to = e - currentTime + 0.1;

        // The setTargetAtTime() method of the AudioParam interface schedules
        // the start of a gradual change to the AudioParam value
        // setTargetAtTime(target, startTime, timeConstant);
        // https://developer.mozilla.org/en-US/docs/Web/API/AudioParam/setTargetAtTime

        // target: The value the parameter will start to transition
        // towards at the given start time

        // startTime: The time that the exponential transition will begin,
        // in the same time coordinate system as AudioContext.currentTime.
        // If it is less than or equal to AudioContext.currentTime,
        // the parameter will start changing immediately.

        // timeConstant: The time-constant value, given in seconds,
        // of an exponential approach to the target value.
        // The larger this value is, the slower the transition will be.

        this.gainNode.gain.setTargetAtTime(
          0,
          audioTime + from / playbackRate,
          0.01
        );
        this.gainNode.gain.setTargetAtTime(
          1,
          audioTime + to / playbackRate,
          0.01
        );
      });
  };

  // Creates a new Audio Context and Gain Node
  connectAudioCtx = async () => {
    // If an AudioContext is already created then we want to resume
    // the context and return early
    if (this.audioCtx) {
      const { currentTime: audioTime } = this.audioCtx;

      // The resume() method of the AudioContext interface resumes
      // the progression of time in an audio context
      // https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/resume

      // Firefox has several issues with its WebAudio implementation. Always
      // calling `.resume()` here helps prevent those, but in Chrome, it
      // creates new issues, so we, with regret, added browser detection.
      if (this.audioCtx.state === 'suspended' || isFirefox) {
        await this.audioCtx.resume();
      }

      this.gainNode.gain.setValueAtTime(1, audioTime);

      return;
    }

    // Create a new Audio Context
    const audioCtx = new AudioContext();

    // Create a MediaElementAudioSourceNode
    // Feed the HTMLMediaElement into it
    const source = audioCtx.createMediaElementSource(this.videoRef);

    // One of the most basic operations you might want to do to
    // a sound is change its volume. Using the Web Audio API, we
    // can route our source to its destination through an GainNode
    // in order to manipulate the volume.
    // https://developer.mozilla.org/en-US/docs/Web/API/GainNode
    const gainNode = audioCtx.createGain();

    // Connect the AudioBufferSourceNode to the gain node.
    source.connect(gainNode);

    // Connect the gain node to the destination.
    gainNode.connect(audioCtx.destination);

    this.audioCtx = audioCtx;
    this.gainNode = gainNode;
  };
}
